import { useEffect, useRef, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { Link as LinkIcon, MessageSquare } from 'lucide-react';
import type { PublicCheckInPlace } from '@rx-manager/shared';
import { BookingApiError } from '../../lib/api';
import { intlLocale, isLocale, useI18n, type Locale, type MessageKey, type Translate } from '../../lib/i18n';
import { CHECKBOX, HEADING, IconTile, cn } from '../../components/ui';

/*
 * Track CIQ — pieces the check-in page (`/i/<code>`), the patient's place in line (`/q/<token>`),
 * the lobby screen (`/l/<token>`) and I'm here on the manage page share: the number, how many are
 * ahead (never minutes, CIQ-D16), "Text me when it's my turn" with the words its yes is given in,
 * and a link that no longer works.
 */

/** One of our own messages with its values, or the server's own sentence. */
export type Failure = { key: MessageKey; vars?: Record<string, string | number> } | { text: string };

export function failureText(failure: Failure, t: Translate): string {
  return 'key' in failure ? t(failure.key, failure.vars) : failure.text;
}

/** The words "Text me when it's my turn" is given in (CIQ-D4), when it can be offered. */
export type TextConsent = PublicCheckInPlace['consent'];

/** `2026-07-15T15:15:00Z` as "9:15 a.m." or "9 h 15", on the place's own clock. */
export function clockTime(iso: string, timeZone: string, locale: string): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return '';
  return new Intl.DateTimeFormat(intlLocale(locale), { hour: 'numeric', minute: '2-digit', timeZone }).format(at);
}

/**
 * A wall-clock time the server sends as `HH:mm` (already on the place's clock), worded as the
 * rest of the page words times: "12:30 a.m.", or "0 h 30".
 */
export function wallClock(hhmm: string, locale: string): string {
  const parts = /^(\d{2}):(\d{2})$/.exec(hhmm);
  if (!parts) return hhmm;
  const at = new Date(Date.UTC(2000, 0, 1, Number(parts[1]), Number(parts[2])));
  return new Intl.DateTimeFormat(intlLocale(locale), { hour: 'numeric', minute: '2-digit', timeZone: 'UTC' }).format(at);
}

/** "14 and 15", or "14, 15 et 16": a party's numbers, in the page's language. */
export function ticketList(tickets: number[], locale: string): string {
  return new Intl.ListFormat(intlLocale(locale), { type: 'conjunction' }).format(tickets.map(String));
}

/** How many are ahead, never how long: "3 people ahead of you", or "You're next". */
export function aheadText(ahead: number, t: Translate): string {
  if (ahead <= 0) return t('checkIn.next');
  return ahead === 1 ? t('checkIn.aheadOne') : t('checkIn.ahead', { n: ahead });
}

/** Everyone's numbers, the first one first; a single number when that is all there is. */
export function ticketsOf(arrival: { ticket: number; tickets: number[] }): number[] {
  return arrival.tickets.length > 0 ? arrival.tickets : [arrival.ticket];
}

/**
 * "Your number is 14", the number large enough to read across a waiting room. The sentence is the
 * catalogue's, so it reads the same in both languages: `t` without values leaves the placeholder
 * in place, and the number goes where it stood. `seat` is the kiosk's "— please take a seat".
 */
export function TicketNumbers({ tickets, seat = false }: { tickets: number[]; seat?: boolean }) {
  const { t, locale } = useI18n();
  const many = tickets.length > 1;
  const key: MessageKey = many
    ? seat
      ? 'checkIn.kioskNumbers'
      : 'checkIn.numbers'
    : seat
      ? 'checkIn.kioskNumber'
      : 'checkIn.number';
  const [before = '', after = ''] = t(key).split(many ? '{tickets}' : '{ticket}');
  return (
    <p className="text-xl font-medium text-heading">
      {before}
      <span className="my-1 block text-6xl font-bold leading-tight tracking-tight tabular-nums text-primary-strong sm:text-7xl">
        {many ? ticketList(tickets, locale) : tickets[0]}
      </span>
      {after}
    </p>
  );
}

/**
 * Where someone who just checked in stands: their number and how many are ahead, said once as it
 * appears. `lead` goes first, inside the same announcement (the manage page's "You're checked in").
 */
export function ArrivalStatus({
  tickets,
  ahead,
  seat = false,
  lead,
}: {
  tickets: number[];
  ahead: number;
  seat?: boolean;
  lead?: ReactNode;
}) {
  const { t } = useI18n();
  return (
    <div role="status" className="space-y-2 text-center">
      {lead}
      <TicketNumbers tickets={tickets} seat={seat} />
      <p className="text-lg font-medium text-foreground">{aheadText(ahead, t)}</p>
    </div>
  );
}

/**
 * "Text me when it's my turn" (CIQ-D4, D17): unticked until the patient ticks it, the mobile it
 * goes to where the page knows it, and the server's own words for the yes, in the language on
 * screen, as small print. The server records the yes in those words; the page never writes its own.
 */
export function TextWhenCalledBox({
  checked,
  onChange,
  digits,
  consent,
}: {
  checked: boolean;
  onChange: (value: boolean) => void;
  /** The last four digits of the mobile on file (Find my visit). */
  digits?: string | null;
  consent: TextConsent;
}) {
  const { t, locale } = useI18n();
  const words = consent ? consent.text[locale].trim() : '';
  const describedBy = digits || words ? 'text-when-called-words' : undefined;
  return (
    <div
      className={cn(
        'space-y-3 rounded-xl border p-4 transition-colors',
        checked ? 'border-primary bg-accent/60' : 'border-input bg-card',
      )}
    >
      <label htmlFor="text-when-called" className="flex min-h-11 cursor-pointer items-start gap-3">
        <input
          id="text-when-called"
          type="checkbox"
          checked={checked}
          onChange={(event) => onChange(event.target.checked)}
          aria-describedby={describedBy}
          className={CHECKBOX}
        />
        <span className="flex items-start gap-2 text-base font-medium text-heading">
          <MessageSquare className="mt-0.5 h-5 w-5 shrink-0 text-primary-strong" aria-hidden="true" />
          {t('checkIn.textMe')}
        </span>
      </label>
      {describedBy && (
        <div id="text-when-called-words" className="space-y-2 pl-8 text-sm text-muted-foreground">
          {digits && <p>{t('checkIn.textTo', { digits })}</p>}
          {words && (
            <>
              <p>{t('details.textRemindersAgree')}</p>
              {/* The server's words, as plain text — never as markup. */}
              <p className="whitespace-pre-wrap text-foreground">{words}</p>
            </>
          )}
        </div>
      )}
    </div>
  );
}

/** A link that no longer works: the page's heading says so, and what to do. */
export function LinkProblem({ message }: { message: MessageKey }) {
  const { t } = useI18n();
  const heading = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    heading.current?.focus();
  }, []);
  return (
    <div className="flex flex-col items-center gap-4 py-6 text-center">
      <IconTile icon={LinkIcon} tone="muted" size="lg" />
      <h1 ref={heading} tabIndex={-1} className={cn(HEADING, 'max-w-md')}>
        {t(message)}
      </h1>
    </div>
  );
}

/**
 * What a check-in refusal says, in the page's language: the server's codes have our own words
 * (its sentences are English only); anything else is its own sentence, or a generic one.
 */
export function checkInFailure(error: unknown): Failure {
  if (!(error instanceof BookingApiError)) return { key: 'error.unknown' };
  if (error.status === 0) return { key: 'error.offline' };
  if (error.status === 429) return { key: 'checkIn.busy' };
  switch (error.code) {
    case 'walk_ins_closed':
      return { key: 'checkIn.walkInsClosed' };
    case 'clinic_full':
      return { key: 'checkIn.clinicFull' };
    case 'already_waiting': {
      const ticket = error.extra.ticket;
      return typeof ticket === 'number' ? { key: 'checkIn.alreadyWaiting', vars: { ticket } } : { text: error.message };
    }
    case 'visit_changed':
      return { key: 'checkIn.visitChanged' };
    case 'too_late':
      return { key: 'checkIn.tooLate' };
    case 'check_in_unavailable':
      return { key: 'checkIn.unavailable' };
    default:
      break;
  }
  // A code made new, or check-in switched off, since the page opened.
  if (error.isMissing) return { key: 'checkIn.missing' };
  return error.message && error.message !== 'unknown' ? { text: error.message } : { key: 'error.unknown' };
}

/**
 * The language a page was opened in, carried to the patient's place in line in the router's
 * state (never the address): the line opens in the language they checked in in.
 */
export function useCarriedLocale(): Locale | undefined {
  const location = useLocation();
  const lang = (location.state as { lang?: unknown } | null)?.lang;
  return typeof lang === 'string' && isLocale(lang) ? lang : undefined;
}

/** A box or a round choice as a whole tile, emerald once chosen. */
export function tile(chosen: boolean, disabled = false): string {
  return cn(
    'flex min-h-12 items-center gap-3 rounded-xl border px-3.5 py-2.5 transition-colors',
    disabled
      ? 'border-border bg-muted/50'
      : chosen
        ? 'cursor-pointer border-primary bg-accent/70'
        : 'cursor-pointer border-input bg-card hover:border-primary/60',
  );
}
